import type {
	CloudMediaSettings,
	CloudRecording,
	CloudRecordingOverviewResponse,
	CloudRecordingParams,
	CloudRecordingResponse,
	GStorageSignedUrlResponse,
} from 'src/components/Camera/types/cloud-recordings';
import { getCameraCloudSignedUrl } from './gcs-auth';
import { cameraCloudApi } from '../http';
import qs from 'qs';

const DELETE_RECORDINGS_TIMEOUT_MS = 60000;

/**
 * Get a signed URL granting read access to the user's cloud recordings.
 *
 * @returns Signed URL response with temporary access credentials
 */
export const getGStorageSignedUrl = async (): Promise<GStorageSignedUrlResponse> => {
	const result = (await cameraCloudApi.get('/auth/gstorage')) as GStorageSignedUrlResponse;
	return result;
};

/**
 * Append signed URL query params to a cloud media URL for authenticated access.
 *
 * @param baseUrl The base URL of the cloud media resource
 * @returns The URL with signed URL query param appended
 */
export const buildCloudMediaUrl = async (baseUrl: string): Promise<string> => {
	if (!baseUrl) return '';

	const token = await getCameraCloudSignedUrl();
	if (!token) return baseUrl;
	const separator = baseUrl.includes('?') ? '&' : '?';
	const result = `${baseUrl}${separator}${token}`;
	return result;
};

/**
 * Get cloud recordings for a specific device.
 *
 * @param deviceId The device ID
 * @param params Optional paging/filter params
 * @returns The device's cloud recordings
 */
export const getCloudRecordings = async (
	deviceId: string,
	params?: CloudRecordingParams,
): Promise<CloudRecordingResponse> => {
	if (!deviceId) return { items: [], limit: 0, offset: 0, total: 0 };
	const queryParams = params ? `?${qs.stringify(params)}` : '';
	const result = (await cameraCloudApi.get(
		`/v1/recordings/device/${deviceId}${queryParams}`,
	)) as CloudRecordingResponse;

	return result;
};

/**
 * Get a per-day overview of a device's cloud recordings, newest day first.
 *
 * @param deviceId The device ID
 * @returns Per-day video counts for the device
 */
export const getDeviceRecordingsOverview = async (
	deviceId: string,
): Promise<CloudRecordingOverviewResponse> => {
	if (!deviceId) return { days: [] };
	const result = (await cameraCloudApi.get(
		`/v1/recordings/device/${deviceId}/overview`,
	)) as CloudRecordingOverviewResponse;

	return result ?? { days: [] };
};

/**
 * Get a single cloud recording by its ID
 *
 * @param recordingId The recording ID
 * @returns The recording object or null if not found
 */
export const getRecordingById = async (recordingId: string): Promise<CloudRecording | null> => {
	if (!recordingId) return null;
	const result = (await cameraCloudApi.get(`/v1/recordings/${recordingId}`)) as CloudRecording;
	return result;
};

export const deleteCloudRecording = async (recordingId: string) => {
	if (!recordingId) return;
	await cameraCloudApi.delete(`/v1/recordings/${recordingId}/`);
};

/**
 * Bulk-delete a device's cloud recordings. With a date (YYYY-MM-DD) only that
 * day is removed, otherwise all recordings for the device are deleted.
 *
 * @param deviceId The device ID
 * @param date Optional calendar day (YYYY-MM-DD) to limit deletion to
 * @returns The number of deleted recordings
 */
export const deleteDeviceRecordings = async (
	deviceId: string,
	date?: string,
): Promise<{ deleted: number }> => {
	const query = date ? `?date=${date}` : '';
	const res = (await cameraCloudApi.delete(`/v1/recordings/device/${deviceId}${query}`, {
		timeout: DELETE_RECORDINGS_TIMEOUT_MS,
	})) as {
		data?: { deleted: number };
	};
	return res?.data ?? { deleted: 0 };
};

/**
 * Get the cloud settings for a device.
 *
 * @param deviceId The device ID
 * @returns The device's cloud settings
 */
export const getDeviceMediaSettings = async (deviceId: string): Promise<CloudMediaSettings | null> => {
	if (!deviceId) return null;
	const result = (await cameraCloudApi.get(
		`/v1/recordings/device/${deviceId}/settings`,
	)) as CloudMediaSettings;
	return result ?? null;
};

// Bulk fetch all latest images for camera devices
export const getAllLatestImages = async () => {
	const response = await cameraCloudApi.post('/v1/recordings/latest-images');
	return response;
};

// Get the latest image for a specific device
export const getDeviceLatestImage = async (deviceId: string) => {
	const response = await cameraCloudApi.get(`/v1/recordings/device/${deviceId}/latest-image`);
	return response;
};
